import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getAdminAnalytics } from "../api/salesApi";

const chartColors = ["#38bdf8", "#34d399", "#fbbf24", "#fb7185", "#a78bfa", "#2dd4bf", "#f97316", "#94a3b8"];

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPeriodRange = (period, anchorDate, customStart, customEnd) => {
  if (period === "custom") return { startDate: customStart, endDate: customEnd };

  const [year, month, day] = anchorDate.split("-").map(Number);
  const anchor = new Date(year, month - 1, day);
  if (period === "day") {
    const date = formatDateInput(anchor);
    return { startDate: date, endDate: date };
  }

  if (period === "week") {
    const start = new Date(anchor);
    const weekday = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - weekday);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { startDate: formatDateInput(start), endDate: formatDateInput(end) };
  }

  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return { startDate: formatDateInput(start), endDate: formatDateInput(end) };
};

const formatCurrency = (amount) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
}).format(Number(amount || 0));

const emptyAnalytics = {
  restaurants: [],
  summary: { totalSales: 0, billCount: 0, averageBill: 0, gstCollected: 0 },
  popularItems: [],
  paymentMix: [],
  salesTrend: [],
};

export default function Admin() {
  const { user } = useAuth();
  const today = formatDateInput(new Date());
  const isSuperAdmin = user?.role === "superadmin";
  const [period, setPeriod] = useState("day");
  const [anchorDate, setAnchorDate] = useState(today);
  const [customStart, setCustomStart] = useState(today);
  const [customEnd, setCustomEnd] = useState(today);
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [selectedProductCategory, setSelectedProductCategory] = useState("all");
  const [analytics, setAnalytics] = useState(emptyAnalytics);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const range = getPeriodRange(period, anchorDate, customStart, customEnd);
  const invalidCustomRange = period === "custom" && (!customStart || !customEnd || customStart > customEnd);

  useEffect(() => {
    if (!user) return undefined;
    if (invalidCustomRange) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");

    getAdminAnalytics({
      ...range,
      restaurantId: selectedRestaurant,
      productCategory: selectedProductCategory,
      signal: controller.signal,
    })
      .then((result) => {
        if (!controller.signal.aborted) setAnalytics(result);
      })
      .catch((requestError) => {
        if (controller.signal.aborted || requestError.code === "ERR_CANCELED") return;
        setAnalytics(emptyAnalytics);
        setError(requestError?.response?.data?.message || "Unable to load dashboard analytics.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [range.startDate, range.endDate, selectedRestaurant, selectedProductCategory, user, invalidCustomRange]);

  const popularItems = analytics.popularItems || [];
  const totalPopularQuantity = popularItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  let pieOffset = 0;
  const pieStops = popularItems.map((item, index) => {
    const segment = totalPopularQuantity ? (Number(item.quantity || 0) / totalPopularQuantity) * 100 : 0;
    const stop = `${chartColors[index % chartColors.length]} ${pieOffset}% ${pieOffset + segment}%`;
    pieOffset += segment;
    return stop;
  });
  const pieStyle = { background: `conic-gradient(${pieStops.join(", ") || "#1e293b 0% 100%"})` };
  const paymentMix = analytics.paymentMix || [];
  const totalPayments = paymentMix.reduce((sum, entry) => sum + Number(entry.value || 0), 0);
  const peakDay = (analytics.salesTrend || []).reduce(
    (peak, entry) => Number(entry.sales || 0) > Number(peak?.sales || 0) ? entry : peak,
    null
  );
  const maxDailySales = Math.max(...(analytics.salesTrend || []).map((entry) => Number(entry.sales || 0)), 1);
  const dateLabelStep = Math.max(1, Math.ceil((analytics.salesTrend || []).length / 7));

  return (
    <div className="min-h-[calc(100vh-178px)] bg-slate-950 px-4 py-5 text-slate-100 md:px-6">
      <div className="mx-auto flex min-w-0 max-w-7xl flex-col gap-5">
        <header className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Operations overview</p>
            <h1 className="mt-1 text-2xl font-semibold text-white">Restaurant analytics</h1>
            <p className="mt-1 text-sm text-slate-400">Settled sales and item performance for the selected period.</p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            {isSuperAdmin ? (
              <label className="flex flex-col gap-1 text-xs font-medium text-slate-400">
                Restaurant
                <select
                  value={selectedRestaurant}
                  onChange={(event) => {
                    setSelectedRestaurant(event.target.value);
                    setSelectedProductCategory("all");
                  }}
                  className="min-w-40 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-400"
                >
                  <option value="all">All restaurants</option>
                  {(analytics.restaurants || []).map((restaurant) => (
                    <option key={restaurant.id} value={String(restaurant.id)}>{restaurant.name}</option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="flex flex-col gap-1 text-xs font-medium text-slate-400">
                Restaurant
                <span className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                  {user?.restaurantName || analytics.restaurants?.[0]?.name || "Assigned restaurant"}
                </span>
              </div>
            )}

            <label className="flex flex-col gap-1 text-xs font-medium text-slate-400">
              Period
              <select
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
                className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-400"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="custom">Custom range</option>
              </select>
            </label>

            {period === "custom" ? (
              <>
                <label className="flex flex-col gap-1 text-xs font-medium text-slate-400">
                  From
                  <input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-slate-400">
                  To
                  <input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
                </label>
              </>
            ) : (
              <label className="flex flex-col gap-1 text-xs font-medium text-slate-400">
                {period === "day" ? "Date" : "Choose a date in period"}
                <input type="date" value={anchorDate} onChange={(event) => setAnchorDate(event.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
              </label>
            )}
          </div>
        </header>

        {error && <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</div>}
        {invalidCustomRange && <div role="alert" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">Select a valid custom date range.</div>}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Settled sales", value: formatCurrency(analytics.summary?.totalSales) },
            { label: "Bills settled", value: Number(analytics.summary?.billCount || 0).toLocaleString("en-IN") },
            { label: "Average bill", value: formatCurrency(analytics.summary?.averageBill) },
            { label: "GST collected", value: formatCurrency(analytics.summary?.gstCollected) },
          ].map((metric, index) => (
            <section key={metric.label} className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className={`mb-3 h-1 rounded-full ${["bg-cyan-400", "bg-emerald-400", "bg-amber-400", "bg-sky-400"][index]}`} />
              <p className="text-sm text-slate-400">{metric.label}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-white">{loading ? "..." : metric.value}</p>
            </section>
          ))}
        </div>

        <div className="grid min-w-0 gap-5 xl:grid-cols-2">
          <section className="min-w-0 rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Menu performance</p>
                <h2 className="mt-1 text-lg font-semibold text-white">Most popular items</h2>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-xs font-medium text-slate-400">
                  Category
                  <select
                    value={selectedProductCategory}
                    onChange={(event) => setSelectedProductCategory(event.target.value)}
                    className="min-w-40 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-400"
                  >
                    <option value="all">All categories</option>
                    {(analytics.categories || []).map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <span className="pb-2 text-xs text-slate-400">Units sold</span>
              </div>
            </div>
            {popularItems.length ? (
              <div className="mt-5 grid items-center gap-6 sm:grid-cols-[minmax(150px,0.8fr)_1.2fr]">
                <div className="mx-auto aspect-square w-full max-w-52 rounded-full p-1" style={pieStyle} role="img" aria-label={`Popular items pie chart for ${popularItems.length} items`}>
                  <div className="flex h-full w-full items-center justify-center rounded-full border-8 border-slate-900 bg-slate-900">
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-white">{totalPopularQuantity.toLocaleString("en-IN")}</p>
                      <p className="text-xs text-slate-400">items sold</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {popularItems.map((item, index) => (
                    <div key={`${item.name}-${index}`} className="flex items-center justify-between gap-3 border-b border-slate-800 pb-2 last:border-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: chartColors[index % chartColors.length] }} />
                        <span className="truncate text-sm text-slate-200" title={item.name}>{item.name}</span>
                      </div>
                      <span className="shrink-0 text-sm font-semibold tabular-nums text-white">{Number(item.quantity).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-5 flex h-52 items-center justify-center rounded-xl border border-dashed border-slate-700 text-sm text-slate-500">
                {loading ? "Loading item sales..." : "No settled item sales in this period."}
              </div>
            )}
          </section>

          <section className="min-w-0 rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Collections</p>
            <h2 className="mt-1 text-lg font-semibold text-white">Payment mix</h2>
            {paymentMix.length ? (
              <div className="mt-5 space-y-4">
                {paymentMix.map((payment, index) => {
                  const value = Number(payment.value || 0);
                  const percentage = totalPayments ? (value / totalPayments) * 100 : 0;
                  return (
                    <div key={payment.name}>
                      <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-300">{payment.name}</span>
                        <span className="tabular-nums text-slate-400">{formatCurrency(value)} · {Math.round(percentage)}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-800" role="progressbar" aria-label={`${payment.name} share`} aria-valuenow={Math.round(percentage)} aria-valuemin="0" aria-valuemax="100">
                        <div className="h-full rounded-full transition-[width]" style={{ width: `${percentage}%`, backgroundColor: chartColors[index % chartColors.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-5 flex h-52 items-center justify-center rounded-xl border border-dashed border-slate-700 text-sm text-slate-500">
                {loading ? "Loading payment totals..." : "No settled payments in this period."}
              </div>
            )}
          </section>
        </div>

        <section className="min-w-0 rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Revenue trend</p>
              <h2 className="mt-1 text-lg font-semibold text-white">Daily settled sales</h2>
            </div>
            {peakDay && <p className="text-xs text-slate-400">Peak day <span className="ml-1 font-semibold text-cyan-300">{peakDay.date} · {formatCurrency(peakDay.sales)}</span></p>}
          </div>
          {(analytics.salesTrend || []).length ? (
            <div className="mt-5 flex h-56 min-w-0 items-end gap-1 border-b border-slate-700 px-1 sm:gap-2">
              {analytics.salesTrend.map((entry, index) => {
                const sales = Number(entry.sales || 0);
                const height = sales > 0 ? Math.max(2, (sales / maxDailySales) * 100) : 0;
                const showLabel = index === 0 || index === analytics.salesTrend.length - 1 || index % dateLabelStep === 0;
                return (
                  <div key={entry.date} className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end" title={`${entry.date}: ${formatCurrency(sales)}`}>
                    <div className="flex h-full w-full items-end">
                      <div className="w-full rounded-t-sm bg-gradient-to-t from-indigo-600 to-cyan-300 transition-[height,filter] group-hover:brightness-125 sm:rounded-t-md" style={{ height: `${height}%` }} />
                    </div>
                    <span className="h-5 pt-1 text-[10px] text-slate-500">{showLabel ? entry.date.slice(-2) : ""}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-5 flex h-56 items-center justify-center rounded-xl border border-dashed border-slate-700 text-sm text-slate-500">
              {loading ? "Loading daily sales..." : "No settled sales in this period."}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}